/**
 * Batch Project Export helpers.
 *
 * Pure helpers used by the Storage Explorer "Batch Export" feature. Unlike the
 * open-project batch export in BatchSubtitleExport.svelte, these functions run
 * against a downloaded project (.ccprj JSON) and therefore take all required
 * context (frame rate, style/window dimensions, trial status) explicitly rather
 * than reading the live editor stores.
 */

import tcLib from "@cclib/lib/timecode.js";
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import offset from "@cclib/functions/events/tcOffset.js";
import tcMultiply from "@cclib/functions/special/tcMultiply.js";
import reorderRtlText from "@cclib/functions/utility/reorderRtlText.js";

const DEFAULT_WINDOW = {
    width: 32,
    height: 15,
    xPadding: 10,
    yPadding: 10,
};

/**
 * Returns true when an event group has at least one exportable event
 * (an event with a start time and some text).
 */
export function isEventGroupValid(eventGroup) {
    if (!eventGroup || !Array.isArray(eventGroup.events) || eventGroup.events.length === 0) {
        return false;
    }

    return eventGroup.events.some((event) => {
        const hasStart = event && event.start !== false && event.start !== null && event.start !== undefined;
        const hasText = event && typeof event.text === "string" && event.text.trim().length > 0;
        return hasStart && hasText;
    });
}

/**
 * Sanitizes a string so it is safe to use as (part of) a filename.
 */
export function sanitizeFileNamePart(value) {
    return String(value ?? "")
        .replace(/[\\/:*?"<>|]/g, "_") // characters illegal in filenames
        .replace(/[\u0000-\u001f]/g, "") // control characters
        .replace(/\s+/g, " ")
        .trim() || "untitled";
}

/**
 * Builds the export filename using Project Name + Event Group Name + Preset Name.
 * e.g. testProject_evgEng_SubRipExport.srt
 */
export function buildExportFileName(projectName, eventGroupName, presetName, ext) {
    return (
        sanitizeFileNamePart(projectName) +
        "_" +
        sanitizeFileNamePart(eventGroupName) +
        "_" +
        sanitizeFileNamePart(presetName) +
        "." +
        ext
    );
}

/**
 * Encodes a single event group using a subtitle preset and returns a downloadable blob.
 *
 * @param {object} eventGroup - The event group to encode (will be deep-cloned).
 * @param {object} preset - A subtitle preset ({ name, config }).
 * @param {object} ctx - Encode context.
 * @param {number} ctx.frameRate - Project frame rate fallback.
 * @param {object} ctx.style - Project style object (window dimensions).
 * @param {string} [ctx.trialStatus] - authState.status; when "in_trial" trial text is applied.
 * @returns {Promise<{ fileBlob: Blob, ext: string }>}
 */
export async function encodeEventGroupWithPreset(eventGroup, preset, ctx = {}) {
    const config = preset.config;
    const style = ctx.style || {};

    const trgExt = config.trgExt;
    const trgProfile = config.trgProfile;
    let trgFrameRate = parseFloat(config.trgFrameRate || ctx.frameRate || defaults.frameRate);
    let trgDropFrame = config.trgDropFrame === "true" || config.trgDropFrame === true;

    // Apply frame rate validation (non-drop-frame rates cannot be drop frame)
    if ([23.976, 24, 25, 30, 50, 60].indexOf(trgFrameRate) > -1) {
        trgDropFrame = false;
    }

    const tcOffset = config.tcOffset || "00:00:00:00";
    const tcOffsetType = config.tcOffsetType || "add";
    const tcMultiplier = config.tcMultiplier || 1;
    const fileEncoding = config.fileEncoding || defaults.encoding;
    const fns = config.fns || "include";
    const reverseRtlStartEndOption = config.reverseRtlStartEndOption || false;
    const useClockTime = config.useClockTime || "yes";

    let trgIncode = config.trgIncode || "00:00:00:00";
    trgIncode = tcLib.tcValidate(trgIncode, trgFrameRate, trgDropFrame);
    const validatedTcOffset = tcLib.tcValidate(tcOffset, trgFrameRate, trgDropFrame);

    let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroup));

    /* FS Filtering START */
    if (fns === "exclude") {
        copyOfEventGroup.events = copyOfEventGroup.events.filter((event) => {
            return !event.flags || event.flags.indexOf("FS") === -1;
        });
    } else if (fns === "only") {
        copyOfEventGroup.events = copyOfEventGroup.events.filter((event) => {
            return event.flags && event.flags.indexOf("FS") > -1;
        });
    }
    /* FS Filtering END */

    /* RTL Formatting */
    if (reverseRtlStartEndOption) {
        copyOfEventGroup = reorderRtlText(copyOfEventGroup);
    }
    /* RTL Formatting END */

    copyOfEventGroup = orderByTime(copyOfEventGroup);
    copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);

    const defaultEncoding = formats.defaultEncodingMap[formats.profileMapping[trgProfile].name];

    /* Incode Math */
    let incode = trgIncode;
    const multiplier =
        trgFrameRate === 23.976 || ((trgFrameRate === 29.97 || trgFrameRate === 59.94) && !trgDropFrame)
            ? 24 / 23.976
            : 1;

    if (useClockTime === "no") {
        const incodeSec = parseFloat(
            (tcLib.tcToSec(trgIncode, trgFrameRate, trgDropFrame, true) * multiplier).toFixed(3)
        );
        incode = tcLib.secToTcClock(incodeSec, trgFrameRate, trgDropFrame);
    }
    /* Incode Math End */

    /* tcOffset */
    if (validatedTcOffset && validatedTcOffset !== "00:00:00:00") {
        copyOfEventGroup = offset(
            copyOfEventGroup,
            validatedTcOffset,
            trgFrameRate,
            trgDropFrame,
            tcOffsetType === "subtract" ? "subtract" : "add"
        );
    }

    if (tcMultiplier && tcMultiplier != 0 && tcMultiplier != 1) {
        copyOfEventGroup = tcMultiply(copyOfEventGroup, tcMultiplier);
    }

    const options = new defaults.options({
        profile: formats.profileMapping[trgProfile].name,
        formatOptions: formats.profileMapping[trgProfile].options.encode,
        frameRate: trgFrameRate,
        dropFrame: trgDropFrame,
        incode: incode || "00:00:00:00",
        target_profile: formats.profileMapping[trgProfile].name,
        source_profile: "closedCaptionProject",
        window: {
            width: style.width ?? DEFAULT_WINDOW.width,
            height: style.height ?? DEFAULT_WINDOW.height,
            xPadding: style.xPadding ?? DEFAULT_WINDOW.xPadding,
            yPadding: style.yPadding ?? DEFAULT_WINDOW.yPadding,
        },
    });

    if (ctx.trialStatus === "in_trial") {
        copyOfEventGroup = replaceTrialText(copyOfEventGroup);
    }

    copyOfEventGroup.events.forEach((event, index, events) => {
        events[index].text = wrapStyledTextWithSpan(event.text);
    });

    const output = await encode(copyOfEventGroup, options);
    let fileBlob;

    if (defaultEncoding === "buffer") {
        if (trgExt === "xlsx") {
            fileBlob = new Blob([output], { type: "application/octet-stream" });
        } else {
            fileBlob = output;
        }
    } else if (defaultEncoding.toLowerCase() === "hex") {
        const byteArray = new Uint8Array(output.match(/.{2}/g).map((e) => parseInt(e, 16)));
        fileBlob = new Blob([byteArray], { type: "application/octet-stream" });
    } else if (defaultEncoding.toLowerCase() === "utf-16le") {
        fileBlob = new Blob([output], { type: "text/plain;charset=utf-16le" });
    } else {
        fileBlob = new Blob([output], { type: "text/plain;charset=" + fileEncoding });
    }

    return { fileBlob, ext: trgExt };
}
